import { Buffer } from 'buffer';

import * as sharedTypes from 'src/actions/shared/sharedTypes';
import createDocumentResource, { handleStandardErrors } from 'src/actions/shared/createDocumentResource';
import { NotExistError } from '@ad-scripts/application/lib/error';

export enum MetadataChoice {
  URL = 'URL',
  DATA = 'DATA',
}

interface RoleMapping {
  roleIds: string[];
}

interface InboxMapping {
  routingGroupIds: string[];
}

interface IdpAttribute {
  attributeKey: string;
  attributeValue: string;
}

export interface AttrToRoleMapping extends RoleMapping, IdpAttribute {}

export interface AttrToInboxMapping extends InboxMapping, IdpAttribute {}

export interface IdpAttributeMappingsConfig {
  defaultRoles: string[];
  defaultInboxes: string[];
  roleMappings: AttrToRoleMapping[];
  inboxMappings: AttrToInboxMapping[];
}

export interface SSOConfig {
  enabled: boolean;
  forceAuthentication: boolean;
  metadata: string;
  metadataOption: MetadataChoice;
  metadataURL: string;
  idpAttributeMappingsConfig?: IdpAttributeMappingsConfig;
  jitUserCreation: boolean;
}

const observerCreator = ({ actions }) => {
  const { broadcast } = actions;

  return ({ dispatch }) => ({
    // transform the item received in the broadcast
    onBroadcast(item) {
      dispatch(broadcast(createSsoConfigFromDto(item)));
    },
    // set the value in the store to be the same as the case of `not_exist` error
    onBroadcastDelete() {
      dispatch(broadcast(createSsoConfigFromDto({ enabled: false })));
    },
  });
};

const ssoConfigResource = createDocumentResource<SSOConfig>({
  name: 'ssoConfig',
  endpoint: 'v1/orgs/:orgId/configuration/sso',
  observerCreator: observerCreator,
});

const { fetchStarted, fetchSuccess, fetchError, fetchRequestError } = ssoConfigResource.actions;

const fetch: sharedTypes.AppThunk =
  () =>
  async (dispatch, getState, { gateways }) => {
    try {
      dispatch(fetchStarted({}));
      const result = await gateways.ssoConfig.request();
      dispatch(fetchSuccess(createSsoConfigFromDto(result)));
    } catch (error) {
      if (error instanceof NotExistError) {
        dispatch(fetchSuccess(createSsoConfigFromDto({ enabled: false })));
      } else {
        handleStandardErrors({
          error,
          dispatch,
          handleDomainError: fetchError,
          handleDefaultError: fetchRequestError,
        });
      }
    }
  };

const createSsoConfigFromDto = dto => {
  const config = {
    enabled: dto.enabled,
    forceAuthentication: dto.forceAuthentication,
    metadataURL: dto.identityProviderMetadataURL,
    metadataOption: dto.identityProviderMetadataURL ? MetadataChoice.URL : MetadataChoice.DATA,
    jitUserCreation: dto.jitUserCreation,
  };

  if (!dto.identityProviderMetadataURL && !dto.identityProviderMetadata) {
    config['metadataOption'] = MetadataChoice.URL;
  }

  if (dto.identityProviderMetadata) {
    config['metadata'] = Buffer.from(dto.identityProviderMetadata, 'base64').toString();
  }

  // Include IDP attribute mappings configuration if it exists
  if (dto.idpAttributeMappingsConfig) {
    config['idpAttributeMappingsConfig'] = dto.idpAttributeMappingsConfig;
  }

  return config;
};

ssoConfigResource.effects.fetch = fetch;
export default ssoConfigResource;
